/* ==========================================================================
   Forgot Password Page
   Task F12/F19: Password reset request with slug-based routing
   Task F30: Updated to use pharmacy colors and PharmacyLogo
   ========================================================================== */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { PharmacyLogo } from '@/components/ui/PharmacyLogo'
import { AccountNotSetUpNotice } from '@/components/auth/AccountNotSetUpNotice'
import { forgotPassword } from '@/api/auth'
import { ApiClientError } from '@/api/client'
import { usePortalSlug } from '@/context/PortalContext'

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

function validateEmail(email: string): string | undefined {
  if (!email.trim()) {
    return 'Email is required'
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address'
  }
  return undefined
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ForgotPassword() {
  const slug = usePortalSlug()

  // Form state
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [touched, setTouched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [apiError, setApiError] = useState<string | undefined>()

  // Handle email change
  const handleEmailChange = (value: string) => {
    setEmail(value)
    setApiError(undefined)
    if (touched) {
      setError(validateEmail(value))
    }
  }

  // Handle blur
  const handleBlur = () => {
    setTouched(true)
    setError(validateEmail(email))
  }

  // Handle form submission
  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate
    const emailError = validateEmail(email)
    setError(emailError)
    setTouched(true)

    if (emailError) {
      return
    }

    setLoading(true)
    setApiError(undefined)

    try {
      await forgotPassword({ email: email.trim() })
      setSubmitted(true)
    } catch (err) {
      if (err instanceof ApiClientError) {
        setApiError(err.getUserMessage())
      } else {
        setApiError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Success state - show confirmation message
  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
        <div className="w-full max-w-md">
          {/* Pharmacy Logo (Task F30) */}
          <div className="flex justify-center mb-8">
            <PharmacyLogo size="xl" linkTo="/" />
          </div>

          <Card className="p-6 sm:p-8">
            {/* Success icon - uses pharmacy primary color */}
            <div className="flex justify-center mb-6">
              <div className="h-16 w-16 rounded-full bg-soft-lavender flex items-center justify-center">
                <Mail className="h-8 w-8 text-[var(--pharmacy-primary)]" aria-hidden="true" />
              </div>
            </div>

            {/* Title */}
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-4">
              Check your email
            </h1>

            {/* Message */}
            <p className="text-center text-charcoal/70 mb-6">
              If an account exists for <strong>{email}</strong>, you will
              receive a password reset link shortly.
            </p>

            <p className="text-center text-sm text-charcoal/60 mb-6">
              Didn't receive the email? Check your spam folder or try again with
              a different email address.
            </p>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setSubmitted(false)
                  setEmail('')
                  setTouched(false)
                  setError(undefined)
                  setApiError(undefined)
                }}
              >
                Try a different email
              </Button>

              <Link to={`/rx/${slug}/login`} className="block">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                  Back to login
                </Button>
              </Link>
            </div>

            {/* Guidance for providers without an account (PORTAL-198) */}
            <AccountNotSetUpNotice className="mt-4" />
          </Card>
        </div>
      </div>
    )
  }

  // Form state
  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="w-full max-w-md">
        {/* Pharmacy Logo (Task F30) */}
        <div className="flex justify-center mb-8">
          <PharmacyLogo size="xl" linkTo="/" />
        </div>

        <Card className="p-6 sm:p-8">
          {/* Title */}
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal text-center mb-2">
            Forgot password?
          </h1>

          {/* Subtitle */}
          <p className="text-center text-charcoal/70 mb-6">
            Enter your email address and we'll send you a link to reset your
            password.
          </p>

          {/* API error alert */}
          {apiError && (
            <Alert variant="error" className="mb-6">
              {apiError}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            {/* Email field */}
            <FormField
              label="Email"
              required
              error={touched ? error : undefined}
              htmlFor="email"
            >
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleBlur}
                error={touched && !!error}
                disabled={loading}
                aria-describedby={error ? 'email-error' : undefined}
              />
            </FormField>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading}
              className="w-full mt-6"
            >
              Send reset link
            </Button>
          </form>

          {/* Back to login link */}
          <div className="mt-6 text-center">
            <Link
              to={`/rx/${slug}/login`}
              className="inline-flex items-center text-sm text-[var(--pharmacy-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pharmacy-secondary)] rounded"
            >
              <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
              Back to login
            </Link>
          </div>

          {/* Guidance for providers without an account (PORTAL-198) */}
          <AccountNotSetUpNotice className="mt-4" />
        </Card>
      </div>
    </div>
  )
}

export default ForgotPassword
